import React, { useContext, useState, useMemo } from "react";

import {
  Card,
  CardActionArea,
  Dialog,
  GlobalStyles,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { IosShare } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { RetroContext } from "./RetrospectiveMain";

const useStyles = makeStyles()((theme) => ({
  outer: {
    height: "100%",
    width: "100%",
    display: "grid",
    gridAutoColumns: "100%",
    gridTemplateRows: "30% 60% 10%",
  },
  header: {
    // vertical
    gridRow: "span 1",
    gridColumn: "span 1",
    marginBottom: theme.spacing(2),
    flexDirection: "column",

    // horizontal
    "@media (orientation: landscape)": {
      height: "20vh",
      flexDirection: "row",
      marginBottom: 0,
    },
  },

  headerText: {
    // vertical
    flex: 0,
    justifyContent: "flex-end",
    flexDirection: "column",

    "& > .MuiTypography-root": {
      textAlign: "center",
    },

    // horizontal
    "@media (orientation: landscape)": {
      textAlign: "start",
      flex: 4,
    },
  },
  headerImg: {
    minHeight: "10vh",
    flex: 1,
    justifyContent: "center",

    "& > img": {
      height: "100%",
    },

    // horizontal
    "@media (orientation: landscape)": {
      minHeight: 0,

      "& > img": {
        height: "auto",
        width: "100%",
      },
    },
  },

  body: {
    gridRow: "span 1",
    gridColumn: "span 1",
  },
  footer: {
    gridRow: "span 1",
    gridColumn: "span 1",
    alignItems: "center",
    justifyContent: "center",
  },
  dialog: {
    width: "60vw",
    maxWidth: "300px",
    height: "50vh",
    maxHeight: "500px",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(3),
    padding: theme.spacing(3),
  },
  card: {
    flex: 1,
    width: "100%",

    "& > button": {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      flex: 1,
      alignItems: "center",
      justifyContent: "space-around",
    },
  },
}));

const SHARE_DATA = {
  title: "Retrospectiva Enermatch",
  url: "https://enermatch.com/retrospectiva2025",
};

export const RetroLayout = ({
  children,
  title,
  subtitle,
  illustrationSrc,
  bgImage,
  bgGradient,
  pageNum,
}: RetroLayoutProps) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { id } = useContext(RetroContext);
  const { classes } = useStyles();
  const canShare = useMemo(
    () => !!(navigator.canShare && navigator.canShare(SHARE_DATA)),
    []
  );
  const feedLink = useMemo(
    () =>
      `https://retrospective-2025.s3.us-east-1.amazonaws.com/${id}/feed_${pageNum}.png`,
    [id, pageNum]
  );
  const storyLink = useMemo(
    () =>
      `https://retrospective-2025.s3.us-east-1.amazonaws.com/${id}/story_${pageNum}.png`,
    [id, pageNum]
  );

  const handleShareButton = async () => {
    setShowDialog(true);
  };

  const handleShare = async (fileLink) => {
    try {
      const imgBlob = await fetch(fileLink).then((res) => res.blob());
      const data = {
        ...SHARE_DATA,
        files: [
          new File([imgBlob], "retrospectiva", {
            type: imgBlob.type,
          }),
        ],
      };
      await navigator.share(data);
    } catch (err) {
      window.open(fileLink);
    }
  };

  return (
    <Grid
      item
      container
      direction="column"
      justifyContent="space-between"
      className={classes.outer}
      id="printArea"
    >
      <GlobalStyles
        styles={{
          body: {
            background: `center / cover no-repeat url(${bgImage})`,
          },
        }}
      />
      <Grid
        item
        container
        className={classes.header}
        alignItems="center"
        justifyContent="space-around"
      >
        <Grid item container className={classes.headerImg}>
          <img src={illustrationSrc} alt="" />
        </Grid>
        <Grid item container direction="column" className={classes.headerText}>
          <Typography variant="h1" color="common.white" sx={{ m: 0 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="common.white">
            {subtitle}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        item
        container
        direction="column"
        justifyContent="center"
        className={classes.body}
      >
        {children}
      </Grid>
      <Grid item container className={classes.footer} gap={1}>
        <img width="20px" src="/static/img/retro2025/miniLogo.png" alt="" />
        <Typography color="common.white" variant="caption" fontWeight="bold">
          enermatch.com/retrospectiva2025
        </Typography>
        <IconButton sx={{ color: "white" }} onClick={handleShareButton}>
          <IosShare />
        </IconButton>
      </Grid>
      <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
        <Grid
          item
          container
          className={classes.dialog}
          sx={{ background: bgGradient }}
        >
          <Card className={classes.card}>
            <CardActionArea
              onClick={() => {
                canShare ? handleShare(feedLink) : window.open(feedLink);
              }}
            >
              <Typography variant="body2">Formato Feed</Typography>
              <Grid
                sx={{
                  height: "100px",
                  width: "100px",
                  borderRadius: "18px",
                  background: bgGradient,
                }}
              />
            </CardActionArea>
          </Card>
          <Card className={classes.card}>
            <CardActionArea
              onClick={() => {
                canShare ? handleShare(storyLink) : window.open(storyLink);
              }}
            >
              <Typography variant="body2">Formato Story</Typography>
              <Grid
                sx={{
                  height: "100px",
                  width: "60px",
                  borderRadius: "18px",
                  background: bgGradient,
                }}
              />
            </CardActionArea>
          </Card>
        </Grid>
      </Dialog>
    </Grid>
  );
};

export type RetroLayoutProps = React.PropsWithChildren<{
  title: string;
  subtitle: string;
  illustrationSrc: string;
  bgImage: string;
  bgGradient: string;
  pageNum: string;
}>;
